import { useGTMDispatch } from '@elgorditosalsero/react-gtm-hook'
import MessageModal from 'components/Modals/MessageModal'
import { ACCREDITED_INVESTOR_INDIVIDUAL, ACCREDITED_INVESTOR_JOINTLY } from 'constants/accreditation'
import { RiseUpErrors } from 'constants/errors'
import useErrorMessage from 'hooks/useErrorMessage'
import { useGTMData } from 'hooks/useGTMData'
import React, { useEffect, useState } from 'react'
import swal from 'sweetalert'
import CompanyLogo from '../../../assets/afbc.png'
import Side7 from '../../../assets/new_side_7.jpg'
import Button from '../../../components/Button'
import LoaderModal from '../../../components/Modals/LoaderModal'
import Stepper from '../../../components/Stepper'
import { useRiseUpAPIV1 } from '../../../hooks/useRiseUpAPIV1'
import { IFormProps } from '../../../interfaces'

const Accreditation1 = (props: IFormProps) => {
  const [isLoading, setIsLoading] = useState(false)
  const [isDisabled, setIsDisabled] = useState(true)
  const [selected, setSelected] = useState({
    accreditationA: {
      statement: '',
      agreed: 'false',
    },
    accreditationB: {
      statement: '',
      agreed: 'false',
    },
    accreditationC: {
      statement: '',
      agreed: 'false',
    },
  })

  const { updateOnboardProgress } = useRiseUpAPIV1()
  const { errorMessage, setErrorMessage } = useErrorMessage()
  const sendDataToGTM = useGTMDispatch()
  const gtmData = useGTMData(props.getValues)

  type AccreditationKey = 'accreditationA' | 'accreditationB' | 'accreditationC'

  const ACCREDITATION_STATEMENTS: Record<AccreditationKey, keyof typeof ACCREDITED_INVESTOR_JOINTLY> = {
    accreditationA: 'NET_WORTH',
    accreditationB: 'INCOME',
    accreditationC: 'CFR_CRITERIA',
  }

  const handleSelection = (e: any) => {
    const id = e.target.id as string

    if (id in ACCREDITATION_STATEMENTS) {
      const key = id as AccreditationKey // Use a type assertion to narrow the type
      const newAgreed = selected[key].agreed === 'true' ? 'false' : 'true'
      const statementKey = ACCREDITATION_STATEMENTS[key]
      const jointOrIndividual =
        props.getValues('investingProfile.option') === 'JOINTLY'
          ? ACCREDITED_INVESTOR_JOINTLY[statementKey]
          : ACCREDITED_INVESTOR_INDIVIDUAL[statementKey]

      setSelected({
        ...selected,
        [key]: {
          statement: jointOrIndividual,
          agreed: newAgreed,
        },
      })
    }
  }

  const validatePage = () => {
    // Iterate through the values of the selected object
    for (const accreditation of Object.values(selected)) {
      // Check if any 'agreed' value is 'true'
      if (accreditation.agreed === 'true') {
        setIsDisabled(false)
        return // Exit the function since we found at least one 'true' value
      }
    }

    // If no 'true' values were found, call setIsDisabled with true
    setIsDisabled(true)
  }

  const handleSubmit = async () => {
    setIsLoading(true)
    props.setValue('investingProfile.selfAccreditation1', selected)
    const data = {
      ...props.getValues(),
      lastCompletedStage: '7',
    }

    try {
      const updateOnboardProgressRes = await updateOnboardProgress(data)

      if (updateOnboardProgressRes.status === 201 || updateOnboardProgressRes.status === 200) {
        sendDataToGTM({
          event: 'funnel_step',
          step_title: 'SELF_ACCREDITATION',
          ...gtmData,
        })
        props.nextStep('7')
      }
    } catch (error: any) {
      setIsLoading(false)
      // catch the duplicate errors
      if (error.response.data === RiseUpErrors.DuplicatePhoneNumber) {
        setErrorMessage(RiseUpErrors.DuplicatePhoneNumber + ' Please update the phone number.')
        return
      }
      if (error.response.data === RiseUpErrors.DuplicateSSN) {
        setErrorMessage(RiseUpErrors.DuplicateSSN + ' Please update the ssn.')
        return
      }
      swal({
        title: 'Error!',
        text: 'Please check if your details are valid and correct. If error persists, contact us.',
        icon: 'error',
      })
    }
  }

  useEffect(() => {
    if (props.getValues('investingProfile.selfAccreditation1')) {
      setSelected(props.getValues('investingProfile.selfAccreditation1'))
    }
  }, []) // eslint-disable-line

  useEffect(() => {
    validatePage()
  }, [selected]) // eslint-disable-line

  return (
    <div className="flex h-screen w-full flex-col md:flex-row md:justify-start">
      <div className="mt-10 flex flex-col sm:w-screen md:mt-0 md:min-h-screen md:w-[45%] lg:w-[40%] xl:w-[35%]">
        <div className="relative w-screen px-10 sm:h-auto sm:px-16 md:hidden">
          <img className="w-screen" src={CompanyLogo} alt="CompanyLogo" />
        </div>
        <div className="relative hidden flex-grow md:flex">
          <img className="flex h-screen w-full" src={Side7} alt={`${Side7}`} />
        </div>
      </div>

      <div className="flex flex-col p-10 sm:w-[100%] md:w-[55%] lg:h-screen lg:w-[60%] lg:overflow-y-scroll lg:p-0 xl:w-[65%]">
        {/* <div className="mb-10 justify-center sm:hidden lg:flex">
          <div className="w-1/2">
            <img className="w-screen" src={CompanyLogo} alt={`${CompanyLogo}`} />
          </div>
        </div> */}

        <div className="md:my-3">
          <Stepper current={props.currentStage} />
        </div>

        <div className="pb-20 text-center text-7xl font-semibold text-gray-400 lg:pb-10 lg:text-left lg:text-5xl">
          {props.getValues('investingProfile.option') === 'JOINTLY'
            ? "We're accredited investors because:"
            : "I'm an accredited investor because:"}
        </div>

        <div className="mt-10 flex flex-col">
          <div className="flex py-4 text-base md:py-1 md:text-lg ">
            <input
              // {...props.registerField('investingProfile.selfAccreditation1.accreditationA')}
              id="accreditationA"
              type="radio"
              checked={selected.accreditationA.agreed === 'true'}
              onClick={(e) => handleSelection(e)}
              onChange={(e) => handleSelection(e)}
              value={'true'}
              className="hidden"
            />
            <label className="ml-3 flex items-center" htmlFor="accreditationA">
              <span
                className={`flex-no-shrink mr-10 inline-block h-16 w-16 rounded-full border border-black lg:h-8 lg:w-8 ${
                  selected.accreditationA.agreed === 'true' ? 'bg-blue-500' : ''
                }`}
              ></span>
            </label>
            {props.getValues('investingProfile.option') === 'JOINTLY'
              ? ACCREDITED_INVESTOR_JOINTLY.NET_WORTH
              : ACCREDITED_INVESTOR_INDIVIDUAL.NET_WORTH}
          </div>

          <div className="flex py-4 text-base md:py-1 md:text-lg ">
            <input
              // {...props.registerField('investingProfile.selfAccreditation1.accreditationB')}
              id="accreditationB"
              type="radio"
              checked={selected.accreditationB.agreed === 'true'}
              onClick={(e) => handleSelection(e)}
              onChange={(e) => handleSelection(e)}
              value={'true'}
              className="hidden"
            />
            <label className="ml-3 flex items-center" htmlFor="accreditationB">
              <span
                className={`flex-no-shrink mr-10 inline-block h-16 w-16 rounded-full border border-black lg:h-8 lg:w-8 ${
                  selected.accreditationB.agreed === 'true' ? 'bg-blue-500' : ''
                }`}
              ></span>
            </label>
            {props.getValues('investingProfile.option') === 'JOINTLY'
              ? ACCREDITED_INVESTOR_JOINTLY.INCOME
              : ACCREDITED_INVESTOR_INDIVIDUAL.INCOME}
          </div>

          <div className="flex py-4 text-4xl lg:py-1 lg:text-lg ">
            <input
              // {...props.registerField('investingProfile.selfAccreditation1.accreditationC')}
              id="accreditationC"
              type="radio"
              checked={selected.accreditationC.agreed === 'true'}
              onClick={(e) => handleSelection(e)}
              onChange={(e) => handleSelection(e)}
              value={'true'}
              className="hidden"
            />
            <label className="ml-3 flex items-center" htmlFor="accreditationC">
              <span
                className={`flex-no-shrink mr-10 inline-block h-16 w-16 rounded-full border border-black lg:h-8 lg:w-8 ${
                  selected.accreditationC.agreed === 'true' ? 'bg-blue-500' : ''
                }`}
              ></span>
            </label>
            {props.getValues('investingProfile.option') === 'JOINTLY'
              ? ACCREDITED_INVESTOR_JOINTLY.CFR_CRITERIA
              : ACCREDITED_INVESTOR_INDIVIDUAL.CFR_CRITERIA}
          </div>
        </div>

        <div className="flex justify-center p-10">
          <div className="flex w-1/2 flex-col">
            <Button btnName={'Back'} onClickHandler={() => props.previousStep('6')} />
          </div>
          <div className="flex w-1/2 flex-col">
            <Button btnName={'Next'} btnType={'button'} onClickHandler={handleSubmit} disabled={isDisabled} />
          </div>
        </div>
      </div>
      <LoaderModal isVisible={isLoading} />
      <MessageModal
        isVisible={errorMessage !== ''}
        onProceed={() => {
          // automatically set the step to appropriate form duplicate error
          if (errorMessage === RiseUpErrors.DuplicatePhoneNumber + ' Please update the phone number.') {
            setErrorMessage('')
            props.nextStep('3')
          }
          if (errorMessage === RiseUpErrors.DuplicateSSN + ' Please update the ssn.') {
            setErrorMessage('')
            props.nextStep('4')
          }
          setErrorMessage('')
        }}
        onCancel={() => {
          setErrorMessage('')
        }}
        title={'Error'}
        description={errorMessage}
        type={'error'}
        btn2={'Ok'}
      />
    </div>
  )
}

export default Accreditation1
